import { $$ } from '../utils/dom.js';

const modalControllers = new WeakMap();
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

function getFocusableElements(container) {
  return $$(FOCUSABLE_SELECTOR, container).filter(
    (element) => !element.disabled,
  );
}

export function initModal() {
  const disposers = [];

  $$('[data-component="modal"]').forEach((modal) => {
    disposers.push(registerModal(modal));
  });

  $$('[data-modal-open]').forEach((btn) => {
    disposers.push(registerOpener(btn));
  });

  return () => {
    disposers.forEach((dispose) => {
      dispose();
    });
  };
}

function registerModal(modal) {
  const dialog = modal.querySelector('.modal__dialog');
  let removeKeydown = null;
  let previousFocus = null;

  const close = () => {
    if (modal.getAttribute('data-state') !== 'open') {
      return;
    }

    if (removeKeydown) {
      removeKeydown();
      removeKeydown = null;
    }

    modal.setAttribute('data-state', 'closed');
    modal.setAttribute('aria-hidden', 'true');

    if (previousFocus && typeof previousFocus.focus === 'function') {
      previousFocus.focus();
    }
    previousFocus = null;
  };

  const open = () => {
    previousFocus = document.activeElement;

    modal.setAttribute('data-state', 'open');
    modal.setAttribute('aria-hidden', 'false');

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }

      if (event.key !== 'Tab' || !dialog) {
        return;
      }

      const focusable = getFocusableElements(dialog);
      if (!focusable.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeydown);
    removeKeydown = () => {
      document.removeEventListener('keydown', onKeydown);
    };

    if (dialog) {
      const focusable = getFocusableElements(dialog);
      if (focusable.length) {
        focusable[0].focus();
      }
    }
  };

  const stopDialogClick = (event) => {
    event.stopPropagation();
  };

  const onBackdropClick = (event) => {
    if (event.target === modal) {
      close();
    }
  };

  dialog?.addEventListener('click', stopDialogClick);
  modal.addEventListener('click', onBackdropClick);

  const closeButtons = $$('[data-modal-close]', modal);
  closeButtons.forEach((btn) => {
    btn.addEventListener('click', close);
  });

  modalControllers.set(modal, { open, close });

  return () => {
    close();
    dialog?.removeEventListener('click', stopDialogClick);
    modal.removeEventListener('click', onBackdropClick);
    closeButtons.forEach((btn) => {
      btn.removeEventListener('click', close);
    });
    modalControllers.delete(modal);
  };
}

function registerOpener(btn) {
  const id = btn.getAttribute('data-modal-open');
  if (!id) {
    return () => {};
  }

  const modal = document.getElementById(id);
  if (!modal) {
    return () => {};
  }

  const onClick = (event) => {
    event.preventDefault();
    modalControllers.get(modal)?.open();
  };

  btn.addEventListener('click', onClick);

  return () => {
    btn.removeEventListener('click', onClick);
  };
}
